import { FunctionComponent, useState } from 'react';
import {
  AggregateSummary,
  ComplianceDataResponse,
  RegulationId,
} from '../types/dashboard.types';
import { FaRegBuilding, FaFileLines } from 'react-icons/fa6';
import { FaChartBar } from 'react-icons/fa';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { useComplianceNavigator } from '../../../../hooks/useComplianceNavigator';
import { Link } from 'react-router-dom';
import ChartViewSection from './ChartViewSection';
import { HalfPieChart } from './HalfPieChart';
import { tailwindColors } from '../../../../../tailwind.config';
import ChartToolTip from '../../../ui/ChartToolTip';
import { ProgressSegment } from '../../../ui/AnimatedProgressBar';
import GenericModal from '../../../ui/GenericModal';
import BaseCurrencyAUM from './BaseCurrencyAUM';
import { cn } from '../../../../utilities/cn';
import { Tooltip } from '@mui/material';
import { StatusPillGroup } from './StatusPill';
import PassRate from './PassRate';
import PeriodCaption from './PeriodCaption';
import { RegulationBreakdownData } from '../types/dashboard.types';
import RsDateRangeToggle from './DateRangeSelector/RsDateRangeToggle';
import { useIsFetching } from '@tanstack/react-query';
import {
  buildSubcategoryPieData,
  hasSubcategoryCounts,
} from '../dashboard-utils';
import { sectionIdFromFundType } from '../../../../types/server-data/ClientConfig';

interface RegulationItemProps extends React.HTMLAttributes<HTMLDivElement> {
  regulation: RegulationId;
  totals: AggregateSummary;
  subsectionCounts?:
  | Record<
    string,
    {
      Pass: number;
      Alert: number;
      Fail: number;
    }
  >
  | undefined;
  totalFailChartData: {
    chartData: {
      name: string;
      value: number;
    }[];
    failPct: number;
  } | null;
  daysInScope: number;
  dataInDateRange: ComplianceDataResponse;
  onToggleChange: (value: { startDate: string; endDate: string }) => void;
  onLoadingChange?: (isLoading: boolean) => void;
  activeCustomRange?: { startDate: string; endDate: string } | null;
  regulationBreakdown?: RegulationBreakdownData | null;
  entityLabel?: string;
}

const RegulationItem: FunctionComponent<RegulationItemProps> = ({
  regulation,
  totals,
  totalFailChartData,
  subsectionCounts,
  daysInScope,
  dataInDateRange,
  onToggleChange,
  onLoadingChange,
  activeCustomRange,
  regulationBreakdown,
  entityLabel = 'ManCo',
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSubcategory, setSelectedSubcategory] = useState<string | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);

  // Check for loading of the currently active range (including preset ranges)
  const isActiveRangeDataLoading =
    useIsFetching({
      predicate: (query) => {
        const key = query.queryKey as readonly unknown[];
        if (
          !Array.isArray(key) ||
          key[0] !== 'dashboard-data-v2' ||
          !key[1] ||
          !key[2]
        ) {
          return false;
        }

        const startDate = key[1] as string;
        const endDate = key[2] as string;

        // Check if this matches the current active range
        if (activeCustomRange) {
          return (
            startDate === activeCustomRange.startDate &&
            endDate === activeCustomRange.endDate
          );
        } else {
          // For preset ranges, calculate the expected dates
          const today = new Date().toISOString().split('T')[0];
          const numDays = daysInScope;
          const expectedStart =
            numDays === 1
              ? today
              : new Date(Date.now() - (numDays - 1) * 24 * 60 * 60 * 1000)
                .toISOString()
                .split('T')[0];

          return startDate === expectedStart && endDate === today;
        }
      },
    }) > 0;

  // Convert your chartData to segments:
  const segments: ProgressSegment[] =
    totalFailChartData?.chartData.map((item) => ({
      label: item.name,
      value: item.value,
      color:
        tailwindColors[
        item.name.toLowerCase() as keyof typeof tailwindColors
        ] || '#cccccc',
    })) || [];

  const performanceResults =
    dataInDateRange.regulation_breakdown[regulation.id]?.status_count.total;

  const modalTitle = (
    <header className="mt-[-6px] flex w-full flex-wrap items-baseline gap-3 pr-10">
      <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-5">
        <h1 className="text-xlg">{regulation.title}</h1>
        {!(isLoading || isActiveRangeDataLoading) && (
          <>
            <span className="text-sm font-medium text-gray-500 underline underline-offset-2">
              {totals.totalReportedFunds} / {totals.totalFunds} Funds
            </span>
            <StatusPillGroup
              data={{
                Fail: performanceResults?.Fail || 0,
                Alert: performanceResults?.Alert || 0,
                Pass: performanceResults?.Pass || 0,
              }}
            />
          </>
        )}
      </div>
      <div className="w-full flex-shrink-0 sm:w-auto">
        <RsDateRangeToggle
          defaultValue={daysInScope}
          options={[
            {
              label: (
                <>
                  <span className="block lg:hidden">7d</span>
                  <span className="hidden lg:block">7 Days</span>
                </>
              ),
              value: 7,
            },
            {
              label: (
                <>
                  <span className="block lg:hidden">30d</span>
                  <span className="hidden lg:block">30 Days</span>
                </>
              ),
              value: 30,
            },
            {
              label: (
                <>
                  <span className="block lg:hidden">90d</span>
                  <span className="hidden lg:block">90 Days</span>
                </>
              ),
              value: 90,
            },
          ]}
          onChange={onToggleChange}
          activeCustomRange={activeCustomRange}
          regulationBreakdown={regulationBreakdown}
        />
      </div>
    </header>
  );

  const chartDisabled = daysInScope === 1;

  const handleModalClose = (open: boolean) => {
    if (!open) {
      setModalOpen(false);
      setSelectedSubcategory(null);
    } else {
      setModalOpen(true);
    }
  };

  const handleSubcategoryClick = (subcategoryName: string) => {
    setSelectedSubcategory(subcategoryName);
    setModalOpen(true);
  };

  return (
    <article className="relative w-full rounded-md border border-gray-200 bg-white p-4 shadow-sm transition">
      <div className="mb-2 flex items-center justify-between border-b border-gray-200 pb-2">
        <h3 className="text-lg font-semibold tracking-wide text-mainblue">
          {regulation.title}
        </h3>
        <div className="ml-2 flex items-center gap-2">
          <Tooltip
            title="Charts available for data over 1 day"
            disableHoverListener={!chartDisabled}
            arrow
          >
            <span>
              <button
                className={cn(
                  'flex items-center gap-1 rounded border px-2 py-1 text-xs font-medium',
                  chartDisabled
                    ? 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400'
                    : 'border-gray-200 text-gray-600 hover:bg-gray-100',
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  if (chartDisabled) return;
                  setSelectedSubcategory(null);
                  setModalOpen(true);
                }}
                disabled={chartDisabled}
              >
                <FaChartBar className="h-2 w-2" />
                Charts
              </button>
            </span>
          </Tooltip>

          {/* Overview Link Button */}
          <Link
            className="flex items-center gap-1 rounded border border-blue-100 px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50"
            to={`/${sectionIdFromFundType(regulation.id)}/overview`}
            title="Go to Regulation Overview"
          >
            <FaExternalLinkAlt className="h-2.5 w-2.5" />
            Overview
          </Link>
          <GenericModal
            isOpen={modalOpen}
            setIsOpen={handleModalClose}
            title={modalTitle}
            className="max-h-[calc(100vh - 4rem)] max-w-[calc(100vw-1rem)] lg:max-w-[calc(100vw-5rem)] 2xl:max-w-[calc(100vw-10rem)]"
          >
            <ChartViewSection
              daysInScope={daysInScope}
              dataInDateRange={dataInDateRange}
              regulation={regulation}
              pageSize={6}
              selectedSubcategory={selectedSubcategory}
              activeCustomRange={activeCustomRange}
              entityLabel={entityLabel}
            />
          </GenericModal>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="flex flex-col gap-4">
        <div className="mt-2 grid grid-cols-1 gap-2 lg:grid-cols-2">
          {' '}
          {/* AUM and Funds Section */}
          <div className="relative w-full rounded-md bg-gray-50/50 p-4">
            <PeriodCaption>By end of chosen period</PeriodCaption>
            <div className="flex flex-col gap-2">
              <BaseCurrencyAUM
                baseCurrency={dataInDateRange.manco_data.base_currency}
                aum={totals.totalAUM}
                title="Total AUM"
              />

              <div className="border-t border-gray-200 pt-2">
                <div className="flex flex-wrap items-start gap-x-6 gap-y-2">
                  <div>
                    <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-gray-500">
                      <FaRegBuilding className="text-blue-500" />
                      Funds
                    </div>
                    <div className="mt-1.5 text-lg font-bold tracking-tight text-gray-900">
                      {totals.totalFunds}
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-gray-500">
                      <FaFileLines className="text-green-500" />
                      Reported
                    </div>
                    <div className="mt-1.5 text-lg font-bold tracking-tight text-gray-900">
                      {totals.totalReportedFunds}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Pie Chart Section */}
          <PassRate
            percentage={100 - (totalFailChartData?.failPct ?? 0)}
            totals={totals}
            segments={segments}
          />
          {/* Sub Categories Section */}
        </div>
        {subsectionCounts && Object.keys(subsectionCounts).length > 0 ? (
          <RegulationSubCategories
            subsectionCounts={subsectionCounts}
            regulation={regulation}
            daysInScope={daysInScope}
            onSubcategoryClick={handleSubcategoryClick}
            dataInDateRange={dataInDateRange}
          />
        ) : null}
      </div>
    </article>
  );
};

export default RegulationItem;

interface RegulationSubCategoriesProps
  extends React.HTMLAttributes<HTMLDivElement> {
  subsectionCounts: Record<
    string,
    {
      Pass: number;
      Alert: number;
      Fail: number;
    }
  >;

  daysInScope: number;
  regulation: RegulationId;
  onSubcategoryClick: (subcategoryName: string) => void;
  dataInDateRange: ComplianceDataResponse;
}
const RegulationSubCategories: FunctionComponent<
  RegulationSubCategoriesProps
> = ({
  subsectionCounts,
  regulation,
  daysInScope,
  onSubcategoryClick,
  dataInDateRange,
  ...props
}) => {
    // Get the subcategories order from the regulation data
    const regulationData = dataInDateRange.regulation_breakdown[regulation.id];
    const subcategoriesOrder = regulationData?.subcategories || [];

    const sortedEntries = Object.entries(subsectionCounts).sort(([a], [b]) => {
      const aIndex = subcategoriesOrder.indexOf(a);
      const bIndex = subcategoriesOrder.indexOf(b);

      // If both are in the order array, sort by their position
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }

      // If only one is in the order array, prioritize it
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;

      // If neither is in the order array, maintain original order
      return 0;
    });

    return (
      <div className="relative w-full rounded-md bg-gray-50/50 p-4" {...props}>
        <PeriodCaption />
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-gray-500">
            <FaChartBar className="text-blue-500" />
            Sub-Category Status
          </div>
          <div className="mt-1.5 flex w-full flex-wrap items-center justify-evenly gap-1.5">
            {sortedEntries.map(([name, counts]) => (
              <RegulationCategory
                key={name}
                name={name}
                counts={counts}
                regulation={regulation}
                daysInScope={daysInScope}
                onSubcategoryClick={onSubcategoryClick}
              />
            ))}
          </div>
        </div>
      </div>
    );
  };

interface RegulationCategoryProps {
  name: string;
  counts: any;
  daysInScope: number;
  regulation: RegulationId;
  onSubcategoryClick: (subcategoryName: string) => void;
}

const RegulationCategory: FunctionComponent<RegulationCategoryProps> = ({
  name,
  counts,
  regulation,
  daysInScope,
  onSubcategoryClick,
}) => {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<'top' | 'bottom'>(
    'top',
  );
  const [tooltipTimeout, setTooltipTimeout] = useState<NodeJS.Timeout | null>(
    null,
  );
  const { getComplianceViewPath } = useComplianceNavigator();

  const chartData = buildSubcategoryPieData(counts);
  const hasNoCounts = !hasSubcategoryCounts(counts);

  const handleMouseEnter = (e: React.MouseEvent) => {
    // Clear any existing timeout
    if (tooltipTimeout) {
      clearTimeout(tooltipTimeout);
      setTooltipTimeout(null);
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const tooltipHeight = 120; // Approximate tooltip height
    const spaceAbove = rect.top;

    // Prefer above, only use below if there's not enough space above
    if (spaceAbove >= tooltipHeight) {
      setTooltipPosition('top');
    } else {
      setTooltipPosition('bottom');
    }

    setIsTooltipVisible(true);
  };

  const handleMouseLeave = () => {
    // Set a small timeout to close the tooltip
    const timeout = setTimeout(() => {
      setIsTooltipVisible(false);
    }, 100); // 100ms delay
    setTooltipTimeout(timeout);
  };

  if (daysInScope <= 1) {
    // For single day views, use React Router Link
    const compliancePath = getComplianceViewPath(regulation.id, name);

    return (
      <Link
        to={compliancePath}
        className="relative flex min-h-[60px] w-[120px] flex-col items-center justify-center rounded-md border border-gray-200 bg-white p-0.5 transition hover:bg-blue-50 hover:shadow-md hover:ring-1 hover:ring-blue-300"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <HalfPieChart data={chartData} size={90} />
        {isTooltipVisible && (
          <div
            className={`absolute left-1/2 z-10 -translate-x-1/2 ${tooltipPosition === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'
              }`}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <ChartToolTip customLabel={name} data={chartData} />
          </div>
        )}
        <span className="mt-0.5 max-w-[80px] overflow-hidden truncate whitespace-nowrap text-center text-[13px] font-medium leading-tight text-gray-600">
          {name}
        </span>
      </Link>
    );
  }

  // For multi-day views, use button behavior (disabled when no counts)
  if (hasNoCounts) {
    return (
      <div
        className="relative flex min-h-[60px] w-[120px] cursor-not-allowed flex-col items-center justify-center rounded-md border border-gray-200 bg-gray-50 p-0.5"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <HalfPieChart data={chartData} size={90} />
        {isTooltipVisible && (
          <div
            className={`absolute left-1/2 z-10 -translate-x-1/2 ${tooltipPosition === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'
              }`}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <ChartToolTip customLabel={name} data={chartData} />
          </div>
        )}
        <span className="mt-0.5 max-w-[80px] overflow-hidden truncate whitespace-nowrap text-center text-[13px] font-medium leading-tight text-gray-400">
          {name}
        </span>
      </div>
    );
  }

  return (
    <button
      key={name}
      role="button"
      className="relative flex min-h-[60px] w-[120px] cursor-pointer flex-col items-center justify-center rounded-md border border-gray-200 bg-white p-0.5 transition hover:bg-blue-50 hover:shadow-md hover:ring-1 hover:ring-blue-300"
      onClick={(e) => {
        e.stopPropagation(); // Avoid bubbling to the parent link
        onSubcategoryClick(name);
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <HalfPieChart data={chartData} size={90} />
      {isTooltipVisible && (
        <div
          className={`absolute left-1/2 z-10 -translate-x-1/2 ${tooltipPosition === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'
            }`}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <ChartToolTip customLabel={name} data={chartData} />
        </div>
      )}
      <span className="mt-0.5 max-w-[80px] overflow-hidden truncate whitespace-nowrap text-center text-[13px] font-medium leading-tight text-gray-600">
        {name}
      </span>
    </button>
  );
};
